import LandingRoute from '../LandingRoute/LandingRoute';
import { lazy, type ReactNode } from 'react';
import { Route } from 'react-router-dom';
import { ModalStateProvider } from 'src/contexts/ModalContext';
import { ModalProvider } from 'src/contexts/ModalContext/ModalContext';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

const DeletedAccountPage = lazy(() =>
  import('src/features/auth/DeletedAccount/DeletedAccountPage').then((module) => ({
    default: module.DeletedAccountPage,
  }))
);
const ExpiredLinkPage = lazy(() => import('src/features/auth/ExpiredLinkPage/ExpiredLinkPage'));
const PostLogoutPage = lazy(() => import('src/features/auth/PostLogoutPage/PostLogoutPage'));
const ResetPasswordPage = lazy(() => import('src/features/auth/ResetPasswordPage/ResetPasswordPage'));
const SuccessfulEmailChangePage = lazy(
  () => import('src/features/auth/SuccessfulEmailChangePage/SuccessfulEmailChangePage')
);
const PublicEmailPreferencesPage = lazy(() => import('src/features/email-preferences/PublicEmailPreferencesPage'));
const LandingPage = lazy(() =>
  import('src/features/landing-page/LandingPage').then((module) => ({ default: module.LandingPage }))
);
const PrivacyPolicyPage = lazy(() => import('src/features/legal/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('src/features/legal/TermsPage'));
const CintRedirectPage = lazy(() => import('src/features/redirect/CintRedirectPage'));
const ProfileSurveyRedirectPage = lazy(() => import('src/features/redirect/ProfileSurveyRedirectPage'));
const FaqPage = lazy(() => import('src/features/support/FaqPage').then((module) => ({ default: module.FaqPage })));
const TermsAndConditionsPage = lazy(() =>
  import('src/features/support/TermsAndConditionsPage').then((module) => ({
    default: module.TermsAndConditionsPage,
  }))
);
const SurveyTipsPage = lazy(() =>
  import('src/features/survey-tips/SurveyTipsPage').then((module) => ({ default: module.SurveyTipsPage }))
);
const ComponentSandbox = lazy(() => import('src/features/testing/ComponentSandbox/ComponentSandbox'));

// Helper function to wrap components with common providers
const withProviders = (component: ReactNode) => (
  <ModalStateProvider>
    <ModalProvider>{component}</ModalProvider>
  </ModalStateProvider>
);

const NotFoundPage = () => {
  const t = useTranslationByKey('PAGES.NOT_FOUND');

  return <h1>{t('TITLE')}</h1>;
};

// Publicly accessible routes that do not require authentication/authorization
export function PublicRoutes() {
  return (
    <>
      <Route path="expired-link" element={<ExpiredLinkPage />} />
      <Route path="successful-email-change" element={<SuccessfulEmailChangePage />} />
      <Route path="logout" element={<PostLogoutPage />} />
      <Route
        path="ga345g53g354hg56243"
        element={
          <ModalStateProvider>
            <ModalProvider>
              <ComponentSandbox />
            </ModalProvider>
          </ModalStateProvider>
        }
      />
      {/* Landing page route for affiliate links and general visitors */}
      <Route
        path="landing"
        element={
          <LandingRoute>
            <ModalStateProvider>
              <ModalProvider>
                <LandingPage />
              </ModalProvider>
            </ModalStateProvider>
          </LandingRoute>
        }
      />
      <Route path="terms-and-conditions" element={withProviders(<TermsAndConditionsPage />)} />
      <Route path="privacy-policy" element={withProviders(<PrivacyPolicyPage />)} />
      <Route path="faq" element={withProviders(<FaqPage />)} />
      <Route path="redirect/cint" element={<CintRedirectPage />} />
      <Route path="redirect/profile-survey" element={<ProfileSurveyRedirectPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route path="survey-tips" element={withProviders(<SurveyTipsPage />)} />
      <Route path="account-deleted" element={<DeletedAccountPage />} />
      <Route path="email-preferences/unsubscribe" element={withProviders(<PublicEmailPreferencesPage />)} />
      <Route path="*" element={<NotFoundPage />} />
    </>
  );
}
